/**
 * GalleryLightbox — the shared ImageLightbox (addendum F-1: extend, do not fork) fed from
 * gallery bytes. Thumbnails load for the strip; the full-size display copy loads for the
 * photo on screen AND its two neighbours, so stepping left or right shows the sharp copy at
 * once instead of the thumbnail sharpening in. Each photo carries its provenance line (F-3).
 *
 * `toolbarExtra` is the slot for the separate "Where was this taken?" lane (F-2).
 *
 * Drawings (F-6) are OPT-IN: only with `showMarkup` does the photo on screen show its drawing and
 * "Draw" (once the full-size display copy has actually loaded, not merely resolved a URL). Without
 * it this viewer never reads the markup, so a new host (say, a customer page) shows none by default.
 * Hosts that pass `showMarkup` are allow-listed in scripts/guards/check-markup-isolation.mjs.
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { ImageLightbox, type LightboxImage } from '../../forms/reports/ImageLightbox';
import { usePrefetchStorageImage, useStorageImage, useStorageImages } from '../../../hooks/fieldService/useStorageImage';
import { GalleryProvenance } from './GalleryProvenance';
import { MarkDataPlateButton } from './MarkDataPlateButton';
import { UnmarkDataPlateButton } from './UnmarkDataPlateButton';
import { PlateComponentControl } from './PlateComponentControl';
import { WrongUnitButton, WrongUnitDialog } from './WrongUnitExit';
import { useMarkupLayer } from './markup/useMarkupLayer';
import type { GeneratorPhoto } from '../../../types/fieldService/generatorPhotos';

interface GalleryLightboxProps {
  photos: GeneratorPhoto[];
  initialIndex: number;
  onClose: () => void;
  toolbarExtra?: (photo: GeneratorPhoto) => ReactNode;
  /** Show and edit drawings (office/tech hosts only). Default false. */
  showMarkup?: boolean;
  /** The unit these photos belong to. With it, a data-plate photo gets "This plate is for:" and any other photo gets "This is a data plate" (office app). */
  generatorDocId?: string;
  /** OFFICE hosts only: offer "Wrong unit" (move the photo to another generator). Default false; never set by the technician strip. */
  allowMove?: boolean;
}

export function GalleryLightbox({ photos, initialIndex, onClose, toolbarExtra, showMarkup = false, generatorDocId, allowMove = false }: GalleryLightboxProps) {
  const [index, setIndex] = useState(initialIndex);
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const thumbPaths = useMemo(
    () => photos.map((p) => p.gallery?.durable.thumb).filter((p): p is string => !!p),
    [photos]
  );
  const thumbs = useStorageImages(thumbPaths);
  const display = useStorageImage(photos[index]?.gallery?.durable.display);
  // Warm the cache for the photos one step away (wrapping, like the viewer's own arrows):
  // bytes only, short retention, no object URL until the viewer actually shows them.
  const count = photos.length;
  usePrefetchStorageImage(count > 1 ? photos[(index + 1) % count]?.gallery?.durable.display : null);
  usePrefetchStorageImage(count > 2 ? photos[(index - 1 + count) % count]?.gallery?.durable.display : null);

  const images: LightboxImage[] = photos.map((p, i) => {
    const thumb = p.gallery ? thumbs[p.gallery.durable.thumb] : undefined;
    return {
      src: (i === index && display.url) || thumb || '',
      alt: `Generator photo ${i + 1}`,
      caption: <GalleryProvenance photo={p} tone="dark" />,
      // The text button reads the sharp display copy, never the 400 px thumbnail this strip may show.
      ocrPath: p.gallery?.durable.display,
    };
  });

  const fullSizeShowing = Boolean(display.url) && loadedSrc === display.url;
  const markup = useMarkupLayer(showMarkup ? photos[index] : undefined, fullSizeShowing);
  const onImageLoad = useCallback((_i: number, src: string) => setLoadedSrc(src), []);
  // "Wrong unit" is mounted only when the host opted in AND named the unit, so other hosts never need a signed-in user.
  const canMove = allowMove && Boolean(generatorDocId);
  const [moving, setMoving] = useState<GeneratorPhoto | null>(null);
  const closeMove = useCallback(() => setMoving(null), []);
  // A landed move closes the whole viewer (onMoved = the host's onClose): the photo left this unit, and the list under the viewer just got shorter.

  const extra = useCallback(
    (i: number) => (
      <>
        {toolbarExtra && photos[i] ? toolbarExtra(photos[i]) : null}
        {generatorDocId && photos[i] ? <MarkDataPlateButton photo={photos[i]} generatorDocId={generatorDocId} /> : null}
        {generatorDocId && photos[i] ? <UnmarkDataPlateButton photo={photos[i]} generatorDocId={generatorDocId} /> : null}
        {generatorDocId && photos[i] ? (
          <PlateComponentControl
            photo={photos[i]}
            generatorDocId={generatorDocId}
            onWrongUnit={canMove ? () => setMoving(photos[i]) : undefined}
          />
        ) : null}
        {canMove && photos[i] ? <WrongUnitButton photo={photos[i]} onOpen={setMoving} /> : null}
        {canMove && generatorDocId && moving && i === index ? (
          <WrongUnitDialog key={moving.id} photo={moving} generatorDocId={generatorDocId} onClose={closeMove} onMoved={onClose} />
        ) : null}
        {i === index ? markup.toolbarButtons : null}
      </>
    ),
    [toolbarExtra, photos, index, markup.toolbarButtons, generatorDocId, canMove, moving, closeMove, onClose]
  );

  return (
    <ImageLightbox
      images={images}
      initialIndex={initialIndex}
      onClose={onClose}
      onIndexChange={setIndex}
      toolbarExtra={extra}
      overlay={(i) => (i === index ? markup.overlay : null)}
      editing={markup.editing}
      editingPanel={markup.panel}
      onImageLoad={onImageLoad}
    />
  );
}
